import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import {SidePanelCard, FaqAccordion, LinkRow } from "../../components/Common/PageWidgets";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";

import arrowRightIconAuth from "../../assets/images/authentication/arrow-right.svg";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const SupportPage = ({ faqs, onContactUs }) => {
  const navigate = useNavigate();
  document.title = "Help & Support | Omaha Food Lovers";

  const [faqsData, setFaqsData] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch FAQs from API
  const fetchFaqs = useCallback(async (page = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoadingFaqs(true);
    }
    
    try {
      const response = await get(`${endpoints.globalFAQs}?page=${page}`);
      
      if (response.data.success && response.data.data?.data) {
        // Transform API response to match component's expected format
        const transformedFaqs = response.data.data.data
          .filter(faq => faq.is_active) // Only show active FAQs
          .map((faq) => ({
            id: faq.id,
            question: faq.question,
            answer: faq.answer,
          }));
        
        // Update pagination state
        const { current_page, last_page } = response.data.data;
        setHasMore(current_page < last_page);
        
        if (isLoadMore) {
          // Append new FAQs to existing data
          setFaqsData(prev => [...prev, ...transformedFaqs]);
        } else {
          // Replace all data on initial load
          setFaqsData(transformedFaqs);
          setCurrentPage(1);
        }
      }
    } catch (err) {
      console.error("Failed to fetch FAQs:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use empty array as fallback
      if (!isLoadMore) {
        setFaqsData([]);
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoadingFaqs(false);
      }
    }
  }, []);

  // Fetch FAQs on mount
  useEffect(() => {
    fetchFaqs(1, false);
  }, [fetchFaqs]);

  // Load more handler for FaqAccordion
  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchFaqs(nextPage, true);
    }
  }, [hasMore, loadingMore, currentPage, fetchFaqs]);

  const handleOpenTerms = () => {
    navigate("/terms-of-service");
  };

  const handleOpenPrivacy = () => {
    navigate("/privacy-policy");
  };

  return (
    <div className="page-content">
      <Container fluid>

        <Row className="g-3 align-items-start">
          <Col xl={8}>
            <SidePanelCard title="Frequently Asked Questions">
              {loadingFaqs ? (
                <div className="faq-accordion">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="faq-item">
                      <div className="faq-item__question">
                        <span className="skeleton-shimmer skeleton-shimmer--small" style={{ width: '70%', height: '20px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : faqsData && faqsData.length > 0 ? (
                <FaqAccordion 
                  items={faqsData} 
                  loadingMore={loadingMore}
                  onLoadMore={handleLoadMore}
                  hasMore={hasMore}
                />
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No FAQs available at the moment.</p>
                </div>
              )}
            </SidePanelCard>
          </Col>

          <Col xl={4}>
            <SidePanelCard title="Legal & Resources">
              <LinkRow label="Terms of Service" onClick={handleOpenTerms} />
              <LinkRow label="Privacy Policy" onClick={handleOpenPrivacy} />
            </SidePanelCard>

            <a href="mailto:info@omahafoodlovers.com" style={{ textDecoration: 'none', display: 'block' }}>
              <CustomButton variant="primary" icon={arrowRightIconAuth}>
                Contact Us
              </CustomButton>
            </a>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

SupportPage.propTypes = {
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ),
  onContactUs: PropTypes.func,
};

SupportPage.defaultProps = {
  onContactUs: () => {},
  faqs: [], // Default to empty array since we fetch from API
};

export default SupportPage;
